import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, LAUNCH_DATE } from "@/lib/api";

const toInputValue = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function KeeperEntrance() {
    const navigate = useNavigate();
    const [key, setKey] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [authed, setAuthed] = useState(false);
    const [signups, setSignups] = useState(null);
    const [launchDate, setLaunchDate] = useState("");
    const [savingDate, setSavingDate] = useState(false);

    const loadDate = async () => {
        try {
            const { data } = await api.get("/settings/launch-date");
            setLaunchDate(toInputValue(data.launch_date ? new Date(data.launch_date) : LAUNCH_DATE));
        } catch {
            setLaunchDate(toInputValue(LAUNCH_DATE));
        }
    };

    const saveDate = async () => {
        if (!launchDate) return;
        setSavingDate(true);
        try {
            await api.post(`/settings/launch-date?key=${encodeURIComponent(key.trim())}`, { launch_date: launchDate });
            window.dispatchEvent(new Event("launch-date-changed"));
            toast.success("The unveiling has been moved.");
        } catch {
            toast.error("Could not move the unveiling. Try again.");
        } finally {
            setSavingDate(false);
        }
    };

    const enter = async (e) => {
        e.preventDefault();
        if (!key.trim()) return;
        setBusy(true);
        try {
            await api.get(`/letters/all?key=${encodeURIComponent(key.trim())}`);
            sessionStorage.setItem("keeper_key", key.trim());
            setAuthed(true);
            setError("");
            loadDate();
        } catch {
            setError("That key does not open the gate.");
        } finally {
            setBusy(false);
        }
    };

    const toggleSignups = async () => {
        if (signups) {
            setSignups(null);
            return;
        }
        try {
            const { data } = await api.get(`/signups?key=${encodeURIComponent(key.trim())}`);
            setSignups(data.signups);
        } catch {
            setError("The list would not open. Try again.");
        }
    };

    return (
        <div
            data-testid="keeper-entrance"
            className="mx-auto mt-10 max-w-xs rounded-sm border border-[rgba(212,175,55,0.25)] bg-[rgba(15,35,24,0.85)] px-5 py-5 backdrop-blur-md"
        >
            {!authed ? (
                <form onSubmit={enter} className="space-y-3">
                    <input
                        type="password"
                        value={key}
                        onChange={(e) => setKey(e.target.value)}
                        placeholder="Keeper's key"
                        data-testid="keeper-entrance-key-input"
                        className="input-eden font-cormorant w-full rounded-sm px-4 py-2.5 text-base"
                    />
                    <button
                        type="submit"
                        disabled={busy}
                        data-testid="keeper-entrance-enter-button"
                        className="btn-eden w-full rounded-sm bg-gradient-to-br from-[rgba(160,120,60,0.9)] to-[rgba(120,85,40,0.9)] px-5 py-2.5 text-[0.6rem] text-[#f5e6c8] disabled:opacity-50"
                    >
                        {busy ? "Opening…" : "Enter"}
                    </button>
                    {error && (
                        <p data-testid="keeper-entrance-error" className="font-cormorant text-sm italic text-[#c84b31]">
                            {error}
                        </p>
                    )}
                </form>
            ) : (
                <div className="mt-4 space-y-3">
                    <button
                        data-testid="keeper-entrance-letters-button"
                        onClick={() => navigate("/keeper")}
                        className="btn-eden w-full rounded-sm bg-gradient-to-br from-[rgba(160,120,60,0.9)] to-[rgba(120,85,40,0.9)] px-5 py-2.5 text-[0.6rem] text-[#f5e6c8]"
                    >
                        Tend the Garden
                    </button>
                    <button
                        data-testid="keeper-entrance-signups-button"
                        onClick={toggleSignups}
                        className="btn-eden w-full rounded-sm border border-[rgba(212,175,55,0.4)] bg-transparent px-5 py-2.5 text-[0.6rem] text-[#d4af37]"
                    >
                        {signups ? "Hide the Emails" : "See the Emails"}
                    </button>
                    <div className="mt-5 border-t border-[rgba(212,175,55,0.2)] pt-4">
                        <p className="font-cinzel text-[0.55rem] tracking-[0.3em] text-[#b89860] uppercase">
                            Change the Calendar Date
                        </p>
                        <input
                            type="datetime-local"
                            value={launchDate}
                            onChange={(e) => setLaunchDate(e.target.value)}
                            data-testid="keeper-launch-date-input"
                            className="input-eden font-cormorant mt-3 w-full rounded-sm px-4 py-2.5 text-base [color-scheme:dark]"
                        />
                        <button
                            onClick={saveDate}
                            disabled={savingDate}
                            data-testid="keeper-launch-date-save"
                            className="btn-eden mt-3 w-full rounded-sm border border-[rgba(212,175,55,0.4)] bg-transparent px-5 py-2.5 text-[0.6rem] text-[#d4af37] disabled:opacity-50"
                        >
                            {savingDate ? "Moving…" : "Set the New Date"}
                        </button>
                    </div>
                    {signups && (
                        <div data-testid="keeper-entrance-signups-list" className="max-h-56 space-y-2 overflow-y-auto pt-2 text-left">
                            <p className="font-cinzel text-[0.55rem] tracking-[0.25em] text-[#a3b899] uppercase">
                                {signups.length} souls waiting
                            </p>
                            {signups.map((s, i) => (
                                <p key={i} data-testid={`keeper-signup-${i}`} className="font-cormorant text-sm italic text-[#d4c4a0]">
                                    {s.email}
                                </p>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
